import { useCurrentUser, useEventLogger } from 'FrontRoyalAngular';
import { fetchBrandConfig } from 'AppBranding';
import { useState, type FC, useMemo, useCallback, memo } from 'react';
import { twMerge } from 'tailwind-merge';
import isChromatic from 'chromatic';
import { Modal } from 'Modal';
import { Skeleton } from 'Skeleton';
import { useVideoStream } from 'Video';
import { type VideoComponentProps, type StudentSpotlightVideoProps } from './StudentSpotlight.types';

const minimizedContainerClasses =
    'relative w-[137.5px] h-[244.44px] shrink-0 overflow-hidden rounded-8 hover:cursor-pointer';
const minimizedStreamClass = 'transition-transform duration-300 ease-in-out';
const expandedStreamClass = 'max-w-[90vw] h-[calc(min(600px,80vh))] max-h-[100%] pt-0 aspect-[9/16]';
const scaleClass = 'scale-125';

export const VideoComponent: FC<VideoComponentProps> = memo(
    ({
        streamId,
        containerClass,
        streamClass = '',
        expanded = false,
        onClick,
        autoPlay = false,
        showInstitutionLogo = false,
        playOnHover = false,
        scaleOnHover = false,
        videoContainerRef,
        shouldLoad,
    }) => {
        const [streamClassName, setStreamClassName] = useState(streamClass);
        const currentUser = useCurrentUser();
        const [player, videoProps, hasLoaded] = useVideoStream({
            streamId,
            muted: !expanded,
            loop: true,
            autoPlay,
            load: shouldLoad,
            playsInline: true,
            controls: expanded,
            className: streamClassName,
            limitQualityToElementSize: !expanded,
            limitBuffer: !expanded,
            // The minimized card never unmutes. The expanded modal is a separate player.
            dropAudio: !expanded,
        });

        const handleStalled = useCallback(() => {
            if (!expanded) return;

            // Handle a bug where the expanded video doesn't play when it's stalled
            // Seemingly because the video resumed playing after buffering, but wasn't triggered to play
            // by a user action. Muting allows playback to continue.
            if (player.current && !player.current.isPaused()) {
                player.current.mute(true);
                player.current.play();
                player.current.mute(false);
            }
        }, [expanded, player]);

        const userBrandConfig = useMemo(() => {
            if (!currentUser?.activeInstitution?.id || !showInstitutionLogo) return null;
            return fetchBrandConfig(currentUser?.activeInstitution.id);
        }, [currentUser?.activeInstitution?.id, showInstitutionLogo]);

        const handleClick = useCallback(() => {
            // Stop the inline video from playing in the background
            if (player.current && !expanded && !autoPlay) {
                player.current.pause();
                player.current.seek(0);
            }
            onClick?.();
        }, [autoPlay, expanded, onClick, player]);
        /**
         * Hover logic
         */
        const mouseEnterHandler = useCallback(() => {
            if (scaleOnHover) setStreamClassName(val => twMerge(val, scaleClass));
            if (!player.current || autoPlay || !playOnHover) return;

            player.current.play();
        }, [autoPlay, playOnHover, player, scaleOnHover]);

        const mouseLeaveHandler = useCallback(() => {
            if (scaleOnHover) setStreamClassName(val => val.replace(scaleClass, ''));
            if (!player.current || autoPlay || !playOnHover) return;

            player.current.pause();
            player.current.seek(0);
        }, [autoPlay, playOnHover, player, scaleOnHover]);

        return (
            <div
                ref={videoContainerRef}
                className={twMerge(containerClass, hasLoaded && 'shadow-smallish hover:shadow-small')}
                onClick={handleClick}
                onKeyDown={e => e.key === 'Enter' && handleClick()}
                onMouseEnter={mouseEnterHandler}
                onMouseLeave={mouseLeaveHandler}
                role="button"
                tabIndex={0}
            >
                {!shouldLoad ? (
                    <div className="h-full w-full bg-content2" />
                ) : !hasLoaded ? (
                    <Skeleton className="h-full w-full" />
                ) : null}

                {shouldLoad && !isChromatic() && (
                    <video {...videoProps} onStalled={handleStalled}>
                        <track kind="captions" />
                    </video>
                )}

                {!expanded && hasLoaded && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                )}

                {userBrandConfig?.acceptedApplicationLogo && hasLoaded && (
                    <div className={twMerge('absolute right-3 top-2 max-w-[70px]', expanded ? 'hidden' : '')}>
                        <img src={userBrandConfig.acceptedApplicationLogo} alt={`${userBrandConfig.branding} logo`} />
                    </div>
                )}
            </div>
        );
    },
);

export const StudentSpotlightVideo: FC<StudentSpotlightVideoProps> = memo(
    ({
        streamId,
        autoplayPreview = false,
        playPreviewOnHover = false,
        scalePreviewOnHover = false,
        showInstitutionLogo = false,
        videoContainerRef,
        shouldLoad,
    }) => {
        const EventLogger = useEventLogger();
        const [expanded, setExpanded] = useState(false);

        const handleClick = useCallback(() => {
            EventLogger.log('student-spotlight:video_clicked', {
                label: streamId,
            });
            setExpanded(true);
        }, [EventLogger, streamId]);

        return (
            <>
                <VideoComponent
                    streamId={streamId}
                    containerClass={minimizedContainerClasses}
                    streamClass={minimizedStreamClass}
                    onClick={handleClick}
                    autoPlay={autoplayPreview}
                    playOnHover={playPreviewOnHover}
                    scaleOnHover={scalePreviewOnHover}
                    showInstitutionLogo={showInstitutionLogo}
                    videoContainerRef={videoContainerRef}
                    shouldLoad={shouldLoad}
                />

                {expanded && (
                    <Modal
                        onClose={() => setExpanded(false)}
                        dismissOnOverlayClick
                        dismissOnEscape
                        hideCloseButton
                        size="unset"
                        duration={0.7}
                        classNames={{
                            body: ['max-h-[80vh]'],
                        }}
                    >
                        <VideoComponent
                            streamId={streamId}
                            expanded
                            autoPlay
                            shouldLoad
                            streamClass={expandedStreamClass}
                        />
                    </Modal>
                )}
            </>
        );
    },
);
